import { extentLinear } from '@d3fc/d3fc-extent';
import {
    AnySeries,
    seriesSvgBar,
    seriesSvgLine,
    seriesSvgMulti,
    seriesSvgPoint,
} from '@d3fc/d3fc-series';
import { ChartDataItem } from './chartData';
import { BaseType, select, selectAll, Selection } from 'd3-selection';
import { BarMouseOver } from './barMouseOver';
import { useMemo } from 'react';
import { OrderFill } from 'widgets/fillsGraph/fillsGraphData.ts';
import isNil from 'lodash/isNil';
import { curveStepAfter } from 'd3-shape';

export const computeYDomain: (data: ChartDataItem[]) => [number, number] = (
    data
) => {
    return data.length > 0
        ? extentLinear().accessors([() => 0, (d: ChartDataItem) => d.volume!])(
              data
          )
        : [0, 1];
};

const volumeSeries = (dataSeries: ChartDataItem[], chartWidth: number) =>
    seriesSvgBar()
        .crossValue((d: ChartDataItem) => d.xIndex)
        .mainValue((d: ChartDataItem) => d.volume)
        .bandwidth(deriveBarBandwidth(chartWidth, dataSeries.length))
        .decorate((selection: HistoricalPercentageChangeSelection) => {
            selection.enter().each((data, index, nodes) => {
                select(nodes[index]).classed('chart-item-' + data.xIndex, true);
            });
            selection.classed('volume-bar', true);
        });

type AveragePriceDataPoint = [number, number];
const averageVolumeSeries = seriesSvgLine()
    .crossValue((d: AveragePriceDataPoint) => d[0])
    .mainValue((d: AveragePriceDataPoint) => d[1])
    .curve(curveStepAfter)
    .decorate((s: HistoricalPercentageChangeSelection) => {
        s.classed('average-volume-line', true);
    });

const mouseOverVolumeSeries = (
    dataSeries: ChartDataItem[],
    chartWidth: number,
    maxVolume: number,
    barMouseOver: BarMouseOver
) =>
    seriesSvgBar()
        .crossValue((d: ChartDataItem) => d.xIndex)
        .mainValue(maxVolume)
        .bandwidth(chartWidth / dataSeries.length)
        .decorate((selection: HistoricalPercentageChangeSelection) => {
            selection.select('path').classed('mouseover-volume-bar', true);
            selection
                .enter()
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .merge(selection as any)
                .each((data, index, nodes) => {
                    const barSelection = select(nodes[index]);

                    barSelection
                        .classed('chart-item-' + data.xIndex, true)
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .merge(barSelection as any);

                    // Listen to mouse events for the legends...
                    barSelection
                        .on('mouseenter', () => {
                            const highlight = selectAll(
                                `.chart-item-${data.xIndex}`
                            );
                            highlight.classed('selected', true);

                            barMouseOver.publish({
                                item: data,
                            });
                        })
                        .on('mouseleave', () => {
                            const highlight = selectAll(
                                `.chart-item-${data.xIndex}`
                            );
                            highlight.classed('selected', false);
                            barMouseOver.publish({});
                        });
                });
        });

export type HistoricalPercentageChangeSelection = Selection<
    BaseType,
    ChartDataItem,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
>;

const priceSeries = seriesSvgLine()
    .crossValue((d: ChartDataItem) => d.xIndex)
    .mainValue((d: ChartDataItem) => d.normalisedPrice)
    .decorate((selection: HistoricalPercentageChangeSelection) => {
        selection.classed('price-line', true);
        selection.enter().each((data, index, nodes) => {
            select(nodes[index]).classed('chart-item-' + data.xIndex, true);
        });
    });

export const closePricePoints = (pricePointSize = 10) => {
    const pointSymbol = {
        draw: function (context: CanvasRenderingContext2D, size: number) {
            context.lineWidth = 1;
            context.arc(0, 0, size / 2, 0, 2 * Math.PI);
            context.closePath();
        },
    };
    return seriesSvgPoint()
        .crossValue((d: ChartDataItem) => d.xIndex)
        .mainValue((d: ChartDataItem) => d.normalisedPrice)
        .size(pricePointSize)
        .type(pointSymbol)
        .decorate((selection: HistoricalPercentageChangeSelection) => {
            selection.classed('price-point', true);
            selection.enter().each((data, index, nodes) => {
                select(nodes[index]).classed('chart-item-' + data.xIndex, true);
            });
        });
};

export const getChartSeries: (
    averageVolume: number | undefined,
    dataSeries: ChartDataItem[],
    chartWidth: number,
    barMouseOver: BarMouseOver,
    pricePointSize?: number
) => AnySeries = (
    averageVolume,
    dataSeries,
    chartWidth,
    barMouseOver,
    pricePointSize
) => {
    const closePricePointsSeries = closePricePoints(pricePointSize);

    const averageVolumeSeriesData =
        !isNil(averageVolume) && dataSeries.length > 0
            ? [
                  [dataSeries[0].xIndex - 1, averageVolume],
                  [dataSeries[dataSeries.length - 1].xIndex + 1, averageVolume],
              ]
            : [];

    return seriesSvgMulti()
        .series([
            volumeSeries(dataSeries, chartWidth),
            closePricePointsSeries,
            priceSeries,
            mouseOverVolumeSeries(
                dataSeries,
                chartWidth,
                Math.max(...dataSeries.map((d) => d.volume ?? 0)),
                barMouseOver
            ),
            averageVolumeSeries,
        ])
        .mapping((data: OrderFill[], index: number, series: AnySeries[]) => {
            switch (series[index]) {
                case averageVolumeSeries:
                    return averageVolumeSeriesData;
                default:
                    return data;
            }
        });
};

function deriveBarBandwidth(width: number, items: number) {
    const sections = items * 2 + 1;
    const sectionWidth = width / sections;
    return Math.min(20, sectionWidth);
}

export function useHistoricalVolumeChartSeries(
    averageVolume: number | undefined,
    dataSeries: ChartDataItem[],
    chartWidth: number,
    barMouseOver: BarMouseOver,
    pricePointSize?: number
) {
    return useMemo(
        () =>
            getChartSeries(
                averageVolume,
                dataSeries,
                chartWidth,
                barMouseOver,
                pricePointSize
            ),
        [averageVolume, dataSeries, chartWidth, barMouseOver, pricePointSize]
    );
}
